import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useEffect, useMemo, useState } from "react";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  CheckCircle2,
  DollarSign,
  Download,
  Eye,
  FilePlus,
  Link2,
  Pencil,
  Plus,
  Send,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  PAYMENT_METHODS,
  fmtMoney,
  dollarsToCents,
  recomputeTotals,
  publicInvoiceUrl,
  type InvoiceStatus,
  methodLabel,
} from "@/lib/invoicing";

import type { InvoicePdfData } from "@/lib/pdf/InvoiceDocument";

export const Route = createFileRoute("/app/invoices_/$invoiceId")({
  head: ({ params }) => ({
    meta: buildAppMeta({
      title: "Invoice details — CoachQuick",
      description: "View invoice line items, payment status, and resend or download the invoice for your client.",
      path: `/app/invoices/${params.invoiceId}`,
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/invoices/${params.invoiceId}` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <InvoiceEditor />
    </RequireAuth>
  ),
});

type LineItem = {
  id: string;
  invoice_id: string;
  session_id: string | null;
  coach_student_id: string | null;
  description: string;
  quantity: number;
  unit_price_cents: number;
  amount_cents: number;
  position: number;
};

type Invoice = {
  id: string;
  coach_id: string;
  coach_student_id: string;
  invoice_number: string;
  status: InvoiceStatus;
  issue_date: string;
  due_date: string | null;
  paid_date: string | null;
  subtotal_cents: number;
  total_cents: number;
  amount_paid_cents: number;
  currency: string;
  notes: string | null;
  payment_instructions: string | null;
  public_token: string;
  created_at?: string | null;
  updated_at?: string | null;
};

type Payment = {
  id: string;
  amount_cents: number;
  paid_at: string | null;
  method: string | null;
  notes: string | null;
};

type DisplayStatus = InvoiceStatus | "overdue";

const STATUS_PILL: Record<DisplayStatus, string> = {
  paid: "bg-[#e6f4ea] text-[#054d28] dark:bg-[#054d28]/30 dark:text-[#7ed492]",
  sent: "bg-[#e8eefb] text-[#1d4ed8] dark:bg-[#1d4ed8]/25 dark:text-[#a5c6ff]",
  partial: "bg-amber-100 text-amber-800",
  overdue: "bg-[#fdebec] text-[#a72027] dark:bg-[#a72027]/20 dark:text-[#f87171]",
  draft: "border border-border text-muted-foreground",
  void: "bg-muted text-muted-foreground",
};

const STATUS_LABEL: Record<DisplayStatus, string> = {
  paid: "Paid",
  sent: "Sent",
  partial: "Partial",
  overdue: "Overdue",
  draft: "Draft",
  void: "Void",
};

const AVATAR_PALETTE = [
  "bg-[#9fe870] text-[#054d28]",
  "bg-[#7dd3fc] text-[#075985]",
  "bg-[#fcd34d] text-[#78350f]",
  "bg-[#fdba74] text-[#7c2d12]",
  "bg-[#f0abfc] text-[#701a75]",
  "bg-[#a5b4fc] text-[#312e81]",
];

function initials(name: string) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function paletteFor(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function fmtLongDate(s: string | null | undefined) {
  if (!s) return "—";
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return s;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function fmtShortDate(s: string | null | undefined) {
  if (!s) return "—";
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return s;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function daysUntil(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  const target = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
}

function lineItemDate(desc: string): string | null {
  const m = desc.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

function stripDateFromDescription(desc: string): string {
  return desc.replace(/\s*[—-]\s*\d{4}-\d{2}-\d{2}\s*$/, "").trim() || desc;
}

function displayStatus(inv: Invoice): DisplayStatus {
  if (inv.status === "sent" || inv.status === "partial") {
    if (inv.due_date) {
      const d = daysUntil(inv.due_date);
      if (d !== null && d < 0) return "overdue";
    }
  }
  return inv.status;
}

function shortLink(url: string) {
  return url.replace(/^https?:\/\//, "");
}

function InvoiceEditor() {
  const { invoiceId } = Route.useParams();
  const { user } = useAuth();
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [items, setItems] = useState<LineItem[]>([]);
  const [client, setClient] = useState<{
    id: string;
    student_name: string;
    student_email: string;
    default_rate_cents: number | null;
  } | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [savingField, setSavingField] = useState(false);
  const [payDialog, setPayDialog] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  async function downloadPdf() {
    if (!invoice || !client) return;
    setDownloadingPdf(true);
    try {
      const { data: cp } = await supabase
        .from("coach_profile" as never)
        .select(
          "business_name, business_address, tax_id, invoice_footer, full_name, connected_email, phone, website",
        )
        .eq("coach_id", invoice.coach_id)
        .maybeSingle();

      const data: InvoicePdfData = {
        invoice: {
          invoice_number: invoice.invoice_number,
          status: invoice.status,
          issue_date: invoice.issue_date,
          due_date: invoice.due_date,
          paid_date: invoice.paid_date,
          subtotal_cents: invoice.subtotal_cents,
          total_cents: invoice.total_cents,
          amount_paid_cents: invoice.amount_paid_cents,
          currency: invoice.currency,
          notes: invoice.notes,
          payment_instructions: invoice.payment_instructions,
        },
        line_items: items.map((li) => ({
          id: li.id,
          description: li.description,
          quantity: li.quantity,
          unit_price_cents: li.unit_price_cents,
          amount_cents: li.amount_cents,
        })),
        coach: {
          business_name: (cp as any)?.business_name ?? null,
          business_address: (cp as any)?.business_address ?? null,
          tax_id: (cp as any)?.tax_id ?? null,
          invoice_footer: (cp as any)?.invoice_footer ?? null,
          full_name: (cp as any)?.full_name ?? null,
          connected_email: (cp as any)?.connected_email ?? null,
          phone: (cp as any)?.phone ?? null,
          website: (cp as any)?.website ?? null,
        },
        client: { name: client.student_name, email: client.student_email },
      };

      const [{ pdf }, { InvoiceDocument }] = await Promise.all([
        import("@react-pdf/renderer"),
        import("@/lib/pdf/InvoiceDocument"),
      ]);
      const blob = await pdf(<InvoiceDocument data={data} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${invoice.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : "Failed to generate PDF");
    } finally {
      setDownloadingPdf(false);
    }
  }

  async function load() {
    if (!user) return;
    setLoading(true);
    const [{ data: inv }, { data: li }, { data: pay }] = await Promise.all([
      supabase.from("invoices" as never).select("*").eq("id", invoiceId).maybeSingle(),
      supabase
        .from("invoice_line_items" as never)
        .select("*")
        .eq("invoice_id", invoiceId)
        .order("position"),
      supabase
        .from("session_payments" as never)
        .select("id, amount_cents, paid_at, method, notes")
        .eq("invoice_id", invoiceId),
    ]);
    if (!inv) {
      setLoading(false);
      return;
    }
    setInvoice(inv as unknown as Invoice);
    setItems((li as unknown as LineItem[]) ?? []);
    setPayments((pay as unknown as Payment[]) ?? []);

    const { data: c } = await supabase
      .from("coach_students")
      .select("id, student_name, student_email, default_rate_cents")
      .eq("id", (inv as any).coach_student_id)
      .maybeSingle();
    setClient(c as any);
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, invoiceId]);

  const totals = useMemo(() => recomputeTotals(items), [items]);
  const balance = (invoice?.total_cents ?? 0) - (invoice?.amount_paid_cents ?? 0);
  const editable = invoice?.status === "draft";

  async function patchInvoice(patch: Partial<Invoice>) {
    if (!invoice) return;
    setSavingField(true);
    const { error } = await supabase
      .from("invoices" as never)
      .update(patch as never)
      .eq("id", invoice.id);
    setSavingField(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setInvoice({ ...invoice, ...patch });
  }

  async function recalcTotalsOnServer(newItems?: LineItem[]) {
    if (!invoice) return;
    const t = recomputeTotals(newItems ?? items);
    await supabase
      .from("invoices" as never)
      .update({ subtotal_cents: t.subtotal_cents, total_cents: t.total_cents } as never)
      .eq("id", invoice.id);
    setInvoice((p) => (p ? { ...p, ...t } : p));
  }

  async function addLineItem(initial?: Partial<LineItem>) {
    if (!invoice) return;
    const position = items.length;
    const defaults = {
      invoice_id: invoice.id,
      description: initial?.description ?? "Coaching session",
      quantity: initial?.quantity ?? 1,
      unit_price_cents: initial?.unit_price_cents ?? client?.default_rate_cents ?? 0,
      session_id: initial?.session_id ?? null,
      coach_student_id: initial?.coach_student_id ?? invoice.coach_student_id ?? null,
      position,
    };
    const amount_cents = Math.round(defaults.quantity * defaults.unit_price_cents);
    const { data, error } = await supabase
      .from("invoice_line_items" as never)
      .insert({ ...defaults, amount_cents } as never)
      .select("*")
      .single();
    if (error) {
      toast.error(error.message);
      return;
    }
    const next = [...items, data as unknown as LineItem];
    setItems(next);
    await recalcTotalsOnServer(next);
  }

  async function pullUnpaidSessions() {
    if (!invoice || !client) return;
    // Find sessions where this client attended (as primary or group attendee)
    const { data: attendeeRows } = await supabase
      .from("session_attendees" as never)
      .select("session_id, attended, sessions!inner(id, session_date, status, coach_id)")
      .eq("coach_student_id", client.id);
    const rowsTyped = (attendeeRows ?? []) as Array<{
      session_id: string;
      attended: boolean | null;
      sessions: { id: string; session_date: string; status: string; coach_id: string } | null;
    }>;
    const candidates0 = rowsTyped
      .filter(
        (r) =>
          r.attended !== false &&
          r.sessions &&
          r.sessions.status === "sent" &&
          r.sessions.coach_id === invoice.coach_id,
      )
      .map((r) => ({ id: r.session_id, session_date: r.sessions!.session_date }));
    if (candidates0.length === 0) {
      toast.info("No sent sessions for this client.");
      return;
    }
    const sessionIds = candidates0.map((s) => s.id);
    const { data: existing } = await supabase
      .from("invoice_line_items" as never)
      .select("session_id, coach_student_id")
      .in("session_id", sessionIds)
      .eq("coach_student_id", client.id);
    const billed = new Set(((existing ?? []) as any[]).map((e) => e.session_id).filter(Boolean));
    const candidates = candidates0.filter((s) => !billed.has(s.id));
    if (candidates.length === 0) {
      toast.info("All sent sessions are already on an invoice.");
      return;
    }
    candidates.sort((a, b) => a.session_date.localeCompare(b.session_date));
    const rate = client.default_rate_cents ?? 0;
    let position = items.length;
    const rows = candidates.map((s) => ({
      invoice_id: invoice.id,
      session_id: s.id,
      coach_student_id: client.id,
      description: `Coaching session — ${s.session_date}`,
      quantity: 1,
      unit_price_cents: rate,
      amount_cents: rate,
      position: position++,
    }));
    const { data, error } = await supabase
      .from("invoice_line_items" as never)
      .insert(rows as never)
      .select("*");
    if (error) {
      toast.error(error.message);
      return;
    }
    const next = [...items, ...((data as unknown as LineItem[]) ?? [])];
    setItems(next);
    await recalcTotalsOnServer(next);
    toast.success(`Added ${candidates.length} session${candidates.length === 1 ? "" : "s"}.`);
  }

  async function updateLineItem(id: string, patch: Partial<LineItem>) {
    const next = items.map((li) => {
      if (li.id !== id) return li;
      const merged = { ...li, ...patch };
      merged.amount_cents = Math.round((merged.quantity || 0) * (merged.unit_price_cents || 0));
      return merged;
    });
    setItems(next);
    const target = next.find((li) => li.id === id)!;
    await supabase
      .from("invoice_line_items" as never)
      .update({
        description: target.description,
        quantity: target.quantity,
        unit_price_cents: target.unit_price_cents,
        amount_cents: target.amount_cents,
      } as never)
      .eq("id", id);
    await recalcTotalsOnServer(next);
  }

  async function removeLineItem(id: string) {
    const next = items.filter((li) => li.id !== id);
    setItems(next);
    await supabase.from("invoice_line_items" as never).delete().eq("id", id);
    await recalcTotalsOnServer(next);
  }

  async function sendInvoice(asReminder = false) {
    if (!invoice || !client) return;
    if (items.length === 0) {
      toast.error("Add at least one line item before sending.");
      return;
    }
    if (!client.student_email) {
      toast.error("Client has no email address.");
      return;
    }
    try {
      const { data: cp } = await supabase
        .from("coach_profile" as never)
        .select("full_name, business_name")
        .eq("coach_id", invoice.coach_id)
        .maybeSingle();
      const coachName =
        (cp as any)?.business_name?.trim() ||
        (cp as any)?.full_name?.trim() ||
        "your coach";

      const subject = `${asReminder ? "Reminder: " : ""}Invoice ${invoice.invoice_number} from ${coachName}`;
      const lines: string[] = [];
      lines.push(`Hi ${client.student_name},`);
      lines.push("");
      lines.push(
        asReminder
          ? `Friendly reminder about invoice ${invoice.invoice_number}.`
          : `Here's your invoice (${invoice.invoice_number}).`,
      );
      lines.push("");
      lines.push(`Issued: ${invoice.issue_date}`);
      if (invoice.due_date) lines.push(`Due: ${invoice.due_date}`);
      lines.push(
        `Total: ${fmtMoney(invoice.total_cents, invoice.currency)}`,
      );
      lines.push("");
      lines.push("Line items:");
      for (const li of items) {
        lines.push(
          `- ${li.description} — ${li.quantity} × ${fmtMoney(li.unit_price_cents, invoice.currency)} = ${fmtMoney(li.amount_cents, invoice.currency)}`,
        );
      }
      lines.push("");
      lines.push(`View invoice online: ${publicInvoiceUrl(invoice.public_token)}`);
      if (invoice.payment_instructions?.trim()) {
        lines.push("");
        lines.push(invoice.payment_instructions.trim());
      }
      lines.push("");
      lines.push("Thanks,");
      lines.push(coachName);

      const body = lines.join("\n");
      const url = `mailto:${encodeURIComponent(client.student_email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

      if (!asReminder && invoice.status === "draft") {
        await patchInvoice({ status: "sent" });
      }
      window.location.href = url;
      toast.success(`Opened email to ${client.student_email}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to open email");
    }
  }

  async function remind() {
    setReminding(true);
    try {
      await sendInvoice(true);
    } finally {
      setReminding(false);
    }
  }

  async function voidInvoice() {
    if (!invoice) return;
    if (!confirm("Void this invoice? It will no longer count toward your reports.")) return;
    await patchInvoice({ status: "void" });
  }

  async function deleteInvoice() {
    if (!invoice) return;
    if (!confirm("Delete this draft invoice permanently?")) return;
    const { error } = await supabase.from("invoices" as never).delete().eq("id", invoice.id);
    if (error) return toast.error(error.message);
    toast.success("Invoice deleted");
    nav({ to: "/app/invoices" });
  }

  function copyPublicLink() {
    if (!invoice) return;
    if (invoice.status === "draft") {
      toast.error("Send the invoice first to share the public link.");
      return;
    }
    const url = publicInvoiceUrl(invoice.public_token);
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied"));
  }

  if (loading) {
    return (
      <AppShell>
        <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="mt-4 h-64 w-full" />
        </div>
      </AppShell>
    );
  }

  if (!invoice) {
    return (
      <AppShell>
        <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
          <p>Invoice not found.</p>
          <Link to="/app/invoices" className="text-brand-text hover:underline">
            ← Back to invoices
          </Link>
        </div>
      </AppShell>
    );
  }

  const ds = displayStatus(invoice);
  const dueDays = daysUntil(invoice.due_date);
  const dueSubLine =
    !invoice.due_date || invoice.status === "paid" || invoice.status === "void"
      ? null
      : dueDays === null
        ? null
        : dueDays > 0
          ? `in ${dueDays} day${dueDays === 1 ? "" : "s"}`
          : dueDays === 0
            ? "due today"
            : `${Math.abs(dueDays)} day${Math.abs(dueDays) === 1 ? "" : "s"} overdue`;
  const overdue = dueDays !== null && dueDays < 0;
  const publicUrl = publicInvoiceUrl(invoice.public_token);
  const settled = balance <= 0;
  const clientName = client?.student_name ?? "Client";

  const timelineEvents: { id: string; label: string; at: string | null; latest: boolean }[] = [];
  if (invoice.paid_date) {
    timelineEvents.push({
      id: "paid",
      label: "Paid in full",
      at: invoice.paid_date,
      latest: false,
    });
  }
  if (invoice.status !== "draft") {
    timelineEvents.push({
      id: "sent",
      label: "Sent to client",
      at: invoice.issue_date,
      latest: false,
    });
  }
  timelineEvents.push({
    id: "created",
    label: "Invoice created",
    at: invoice.created_at ?? invoice.issue_date,
    latest: false,
  });
  if (timelineEvents.length > 0) timelineEvents[0].latest = true;

  return (
    <AppShell>
      <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
        {/* Breadcrumb */}
        <Link
          to="/app/invoices"
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Invoices
        </Link>

        {/* Header */}
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-display font-mono text-[34px] font-black tracking-tight text-foreground md:text-[38px]">
                {invoice.invoice_number}
              </h1>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_PILL[ds]}`}
              >
                {STATUS_LABEL[ds]}
              </span>
            </div>
            {client && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <span className="text-xs uppercase tracking-wider">For</span>
                <Avatar className="h-7 w-7">
                  <AvatarFallback className={`text-[10px] font-bold ${paletteFor(clientName)}`}>
                    {initials(clientName)}
                  </AvatarFallback>
                </Avatar>
                <Link
                  to="/app/clients/$clientId"
                  params={{ clientId: client.id }}
                  className="font-semibold text-foreground hover:underline"
                >
                  {clientName}
                </Link>
                <span>· {client.student_email}</span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="rounded-[10px]"
              onClick={downloadPdf}
              disabled={downloadingPdf}
            >
              <Download className="h-3.5 w-3.5" /> {downloadingPdf ? "Preparing…" : "PDF"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-[10px]"
              onClick={copyPublicLink}
            >
              <Link2 className="h-3.5 w-3.5" /> Copy link
            </Button>
            {invoice.status !== "draft" && invoice.status !== "paid" && invoice.status !== "void" && (
              <Button
                size="sm"
                variant="outline"
                className="rounded-[10px]"
                onClick={remind}
                disabled={reminding}
              >
                <Send className="h-3.5 w-3.5" /> {reminding ? "Sending…" : "Remind"}
              </Button>
            )}
            {editable && (
              <Button onClick={() => sendInvoice(false)} className="rounded-[10px]">
                <Send className="h-4 w-4" /> Send
              </Button>
            )}
            {balance > 0 && invoice.status !== "void" && (
              <Button onClick={() => setPayDialog(true)} className="rounded-[10px]">
                <CheckCircle2 className="h-4 w-4" /> Mark paid
              </Button>
            )}
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1.8fr_1fr]">
          {/* Left column */}
          <div className="space-y-4">
            {/* Issued / Due / Link strip + Line items + totals */}
            <div className="overflow-hidden rounded-[16px] border bg-card">
              <div className="grid grid-cols-1 divide-y border-b sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                <div className="px-5 py-4">
                  <p className="text-xs text-muted-foreground">Issued</p>
                  <p className="mt-1 text-sm font-bold">{fmtLongDate(invoice.issue_date)}</p>
                  {editable && (
                    <Input
                      type="date"
                      value={invoice.issue_date}
                      onChange={(e) =>
                        setInvoice({ ...invoice, issue_date: e.target.value })
                      }
                      onBlur={(e) => patchInvoice({ issue_date: e.target.value })}
                      className="mt-1.5 h-7 w-full text-xs"
                    />
                  )}
                </div>
                <div className="px-5 py-4">
                  <p className="text-xs text-muted-foreground">Due</p>
                  <p className="mt-1 text-sm font-bold">{fmtLongDate(invoice.due_date)}</p>
                  {dueSubLine && (
                    <p
                      className={`mt-0.5 text-[11px] font-semibold ${
                        overdue
                          ? "text-[#a72027] dark:text-[#f87171]"
                          : "text-[#b86700] dark:text-[#fcd34d]"
                      }`}
                    >
                      {dueSubLine}
                    </p>
                  )}
                  {(editable || invoice.status === "sent" || invoice.status === "partial") && (
                    <Input
                      type="date"
                      value={invoice.due_date ?? ""}
                      onChange={(e) =>
                        setInvoice({ ...invoice, due_date: e.target.value || null })
                      }
                      onBlur={(e) => patchInvoice({ due_date: e.target.value || null })}
                      className="mt-1.5 h-7 w-full text-xs"
                    />
                  )}
                </div>
                <div className="px-5 py-4">
                  <p className="text-xs text-muted-foreground">Shareable link</p>
                  <p className="mt-1 truncate text-sm font-mono text-foreground">
                    {shortLink(publicUrl)}
                  </p>
                  <button
                    type="button"
                    onClick={copyPublicLink}
                    className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
                  >
                    <Link2 className="h-3 w-3" /> Copy
                  </button>
                </div>
              </div>

              <div className="px-5 pt-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-sm font-bold tracking-tight">Line items</h2>
                  {editable && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="rounded-[8px] text-xs text-muted-foreground hover:text-foreground"
                        onClick={pullUnpaidSessions}
                      >
                        <FilePlus className="h-3.5 w-3.5" /> Pull from sessions
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-[8px] text-xs"
                        onClick={() => addLineItem()}
                      >
                        <Plus className="h-3.5 w-3.5" /> Add line
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {items.length === 0 ? (
                <p className="px-5 py-6 text-sm text-muted-foreground">
                  No items yet — add a line or pull in sessions.
                </p>
              ) : (
                <table className="mt-3 w-full text-sm">
                  <thead>
                    <tr className="border-b text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      <th className="w-24 px-5 py-2 text-left">Date</th>
                      <th className="px-5 py-2 text-left">Description</th>
                      <th className="w-20 px-5 py-2 text-right">Hrs</th>
                      <th className="w-24 px-5 py-2 text-right">Rate</th>
                      <th className="w-28 px-5 py-2 text-right">Amount</th>
                      {editable && <th className="w-10 px-3"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((li) => {
                      const dateGuess = lineItemDate(li.description);
                      const descClean = stripDateFromDescription(li.description);
                      return (
                        <tr key={li.id} className="border-b last:border-0">
                          <td className="px-5 py-3 text-muted-foreground">
                            {dateGuess ? fmtShortDate(dateGuess) : "—"}
                          </td>
                          <td className="px-5 py-3">
                            {editable ? (
                              <Input
                                value={li.description}
                                onChange={(e) =>
                                  setItems(
                                    items.map((x) =>
                                      x.id === li.id
                                        ? { ...x, description: e.target.value }
                                        : x,
                                    ),
                                  )
                                }
                                onBlur={(e) =>
                                  updateLineItem(li.id, { description: e.target.value })
                                }
                                className="h-8 border-transparent bg-transparent text-sm focus-visible:border-border focus-visible:bg-card"
                              />
                            ) : (
                              descClean
                            )}
                          </td>
                          <td className="px-5 py-3 text-right tabular-nums">
                            {editable ? (
                              <DecimalCellInput
                                value={li.quantity}
                                format={(n) => String(n)}
                                minOnBlur={1}
                                onChange={(n) =>
                                  setItems(
                                    items.map((x) =>
                                      x.id === li.id ? { ...x, quantity: n } : x,
                                    ),
                                  )
                                }
                                onCommit={(n) => updateLineItem(li.id, { quantity: n })}
                                className="ml-auto h-8 w-20 border-transparent bg-transparent text-right text-sm focus-visible:border-border focus-visible:bg-card"
                              />
                            ) : (
                              li.quantity
                            )}
                          </td>
                          <td className="px-5 py-3 text-right tabular-nums">
                            {editable ? (
                              <DecimalCellInput
                                value={li.unit_price_cents / 100}
                                format={(n) => n.toFixed(2)}
                                minOnBlur={0}
                                onChange={(n) =>
                                  setItems(
                                    items.map((x) =>
                                      x.id === li.id
                                        ? { ...x, unit_price_cents: Math.round(n * 100) }
                                        : x,
                                    ),
                                  )
                                }
                                onCommit={(n) =>
                                  updateLineItem(li.id, {
                                    unit_price_cents: Math.round(n * 100),
                                  })
                                }
                                className="ml-auto h-8 w-24 border-transparent bg-transparent text-right text-sm focus-visible:border-border focus-visible:bg-card"
                              />
                            ) : (
                              fmtMoney(li.unit_price_cents, invoice.currency)
                            )}
                          </td>
                          <td className="px-5 py-3 text-right font-bold tabular-nums">
                            {fmtMoney(li.amount_cents, invoice.currency)}
                          </td>
                          {editable && (
                            <td className="px-3 py-3">
                              <button
                                type="button"
                                onClick={() => removeLineItem(li.id)}
                                aria-label="Remove line item"
                                className="text-muted-foreground hover:text-destructive"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* Totals */}
              <div className="flex justify-end px-5 py-5">
                <div className="w-full max-w-[320px] space-y-2 text-sm">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Subtotal</span>
                    <span>{fmtMoney(totals.subtotal_cents, invoice.currency)}</span>
                  </div>
                  <div className="flex items-baseline justify-between border-y py-2">
                    <span className="text-sm font-bold">Total</span>
                    <span className="font-display text-[22px] font-black tracking-tight">
                      {fmtMoney(totals.total_cents, invoice.currency)}
                    </span>
                  </div>
                  {invoice.amount_paid_cents > 0 && (
                    <div className="flex justify-between text-[#054d28] dark:text-[#7ed492]">
                      <span>Paid</span>
                      <span className="font-semibold">
                        {fmtMoney(invoice.amount_paid_cents, invoice.currency)}
                      </span>
                    </div>
                  )}
                  <div
                    className={`mt-1 flex items-center justify-between rounded-[10px] px-3 py-2 ${
                      settled
                        ? "bg-[#e6f4ea] dark:bg-[#054d28]/30"
                        : "bg-[#fff3c2] dark:bg-[#b86700]/20"
                    }`}
                  >
                    <span
                      className={`text-sm font-bold ${
                        settled
                          ? "text-[#054d28] dark:text-[#7ed492]"
                          : "text-[#b86700] dark:text-[#fcd34d]"
                      }`}
                    >
                      {settled ? "Settled" : "Balance due"}
                    </span>
                    <span
                      className={`font-display text-lg font-black tracking-tight ${
                        settled
                          ? "text-[#054d28] dark:text-[#7ed492]"
                          : "text-[#b86700] dark:text-[#fcd34d]"
                      }`}
                    >
                      {fmtMoney(settled ? 0 : balance, invoice.currency)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Activity timeline */}
            <div className="rounded-[16px] border bg-card p-5">
              <h2 className="text-sm font-bold tracking-tight">Activity</h2>
              <ul className="mt-4 space-y-3">
                {timelineEvents.map((ev, i) => (
                  <li key={ev.id} className="relative flex items-start gap-3">
                    <div className="relative flex flex-col items-center">
                      <span
                        className={`flex h-[30px] w-[30px] items-center justify-center rounded-full ${
                          ev.latest
                            ? "bg-[#9fe870] text-[#054d28]"
                            : "bg-[#e8ebe6] text-muted-foreground dark:bg-white/10"
                        }`}
                      >
                        {ev.id === "paid" ? (
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        ) : ev.id === "sent" ? (
                          <Send className="h-3.5 w-3.5" />
                        ) : ev.id === "created" ? (
                          <FilePlus className="h-3.5 w-3.5" />
                        ) : (
                          <Eye className="h-3.5 w-3.5" />
                        )}
                      </span>
                      {i < timelineEvents.length - 1 && (
                        <span className="mt-1 h-6 w-[2px] bg-border" />
                      )}
                    </div>
                    <div className="flex-1 leading-tight">
                      <p className="text-sm font-semibold">{ev.label}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {fmtLongDate(ev.at)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right sidebar */}
          <div className="space-y-4">
            {/* Payment instructions */}
            <div className="rounded-[16px] border bg-card p-5">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold tracking-tight">Payment instructions</h2>
                <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                  Shown to client
                </span>
              </div>
              <Textarea
                rows={4}
                className="mt-3 resize-none rounded-[10px] text-sm"
                placeholder="e.g. Venmo @coach-jane or Zelle to jane@example.com"
                value={invoice.payment_instructions ?? ""}
                onChange={(e) =>
                  setInvoice({ ...invoice, payment_instructions: e.target.value })
                }
                onBlur={(e) => patchInvoice({ payment_instructions: e.target.value })}
              />
            </div>

            {/* Internal notes */}
            <div className="rounded-[16px] border bg-card p-5">
              <h2 className="text-sm font-bold tracking-tight">Internal notes</h2>
              <Textarea
                rows={3}
                className="mt-3 resize-none rounded-[10px] text-sm"
                placeholder="For your eyes only"
                value={invoice.notes ?? ""}
                onChange={(e) => setInvoice({ ...invoice, notes: e.target.value })}
                onBlur={(e) => patchInvoice({ notes: e.target.value })}
              />
              <p className="mt-2 text-[11px] text-muted-foreground">
                Only you can see these.
              </p>
            </div>

            {/* Payments recorded */}
            <div className="rounded-[16px] border bg-card p-5">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold tracking-tight">Payments recorded</h2>
                {invoice.status !== "void" && (
                  <button
                    type="button"
                    onClick={() => setPayDialog(true)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
                  >
                    <Plus className="h-3 w-3" /> Add
                  </button>
                )}
              </div>
              {payments.length === 0 ? (
                <div className="mt-3 rounded-[10px] bg-[#e8ebe6] p-5 text-center dark:bg-white/5">
                  <p className="text-sm font-semibold">No payments yet</p>
                  {invoice.status !== "void" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3 rounded-[8px]"
                      onClick={() => setPayDialog(true)}
                    >
                      <DollarSign className="h-3.5 w-3.5" /> Record a payment
                    </Button>
                  )}
                </div>
              ) : (
                <ul className="mt-3 divide-y divide-border">
                  {payments.map((p) => (
                    <li key={p.id} className="py-2 text-sm">
                      <p className="font-semibold">
                        {fmtMoney(p.amount_cents, invoice.currency)} via {methodLabel(p.method)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {p.paid_at ? new Date(p.paid_at).toLocaleDateString() : "Unpaid"}
                        {p.notes ? ` · ${p.notes}` : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Danger zone */}
            {invoice.status !== "void" && (
              <div className="rounded-[16px] bg-[#0e0f0c] p-5 text-white">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#9fe870]">
                  Danger zone
                </p>
                <p className="mt-2 text-xs text-white/70">
                  Voiding sets the balance to $0 and notifies the client. The invoice stays in
                  your history for reference.
                </p>
                <button
                  type="button"
                  onClick={editable ? deleteInvoice : voidInvoice}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-[8px] bg-white/10 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/20"
                >
                  {editable ? (
                    <>
                      <Trash2 className="h-3.5 w-3.5" /> Delete draft
                    </>
                  ) : (
                    <>
                      <Pencil className="h-3.5 w-3.5" /> Void invoice
                    </>
                  )}
                </button>
              </div>
            )}

            {savingField && <p className="text-xs text-muted-foreground">Saving…</p>}
          </div>
        </div>
      </div>

      {payDialog && (
        <RecordPaymentDialog
          invoice={invoice}
          balance={balance}
          onClose={() => setPayDialog(false)}
          onSaved={() => {
            setPayDialog(false);
            load();
          }}
        />
      )}
    </AppShell>
  );
}

function RecordPaymentDialog({
  invoice,
  balance,
  onClose,
  onSaved,
}: {
  invoice: Invoice;
  balance: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [amount, setAmount] = useState((balance / 100).toFixed(2));
  const [method, setMethod] = useState("venmo");
  const [paidAt, setPaidAt] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  async function save() {
    const cents = dollarsToCents(amount);
    if (cents <= 0) return toast.error("Enter an amount");
    setBusy(true);
    try {
      const { error } = await supabase.from("session_payments" as never).insert({
        coach_id: invoice.coach_id,
        invoice_id: invoice.id,
        session_id: null,
        amount_cents: cents,
        currency: invoice.currency,
        method,
        paid_at: new Date(paidAt).toISOString(),
        notes: notes || null,
      } as never);
      if (error) throw error;

      const newPaid = invoice.amount_paid_cents + cents;
      const fullyPaid = newPaid >= invoice.total_cents;
      await supabase
        .from("invoices" as never)
        .update({
          amount_paid_cents: newPaid,
          status: fullyPaid ? "paid" : "partial",
          paid_date: fullyPaid ? paidAt : invoice.paid_date,
        } as never)
        .eq("id", invoice.id);
      toast.success("Payment recorded");
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to record payment");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:rounded-[16px]">
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="amt">Amount</Label>
              <Input
                id="amt"
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="m">Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="pd">Paid on</Label>
            <Input
              id="pd"
              type="date"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label htmlFor="pn">Notes (optional)</Label>
            <Input
              id="pn"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1.5"
              placeholder="Confirmation #, memo, etc."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Record"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DecimalCellInput({
  value,
  onChange,
  onCommit,
  minOnBlur = 0,
  format = (n: number) => String(n),
  className,
}: {
  value: number;
  onChange: (n: number) => void;
  onCommit: (n: number) => void;
  minOnBlur?: number;
  format?: (n: number) => string;
  className?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? format(value);
  return (
    <Input
      type="text"
      inputMode="decimal"
      value={shown}
      onFocus={(e) => {
        setDraft(format(value));
        e.currentTarget.select();
      }}
      onChange={(e) => {
        const v = e.target.value;
        if (v !== "" && !/^\d*\.?\d*$/.test(v)) return;
        setDraft(v);
        const n = parseFloat(v);
        if (isFinite(n)) onChange(n);
      }}
      onBlur={(e) => {
        const parsed = parseFloat(e.target.value);
        const valid =
          isFinite(parsed) && (minOnBlur > 0 ? parsed >= minOnBlur : parsed >= 0);
        const next = valid ? parsed : minOnBlur;
        setDraft(null);
        onChange(next);
        onCommit(next);
      }}
      className={className}
    />
  );
}
